import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import MultimediaService from '../services/multimediaService';
import { useFormControls } from '../../../common/hooks/useFormControls';

import EmbedInput from './EmbedInput';
import FileDropZone from '../../../common/components/FileDropZone/FileDropZone';
import UploadedFileContainer from './UploadedFileContainer';
import MultimediaUploadErrorBanner from './MultimediaUploadErrorBanner';
import { flattenFileRejections } from '../utils/multimediaFormHelpers';
import LoadingScreen from '../../../common/LoadingScreen';
import { fileLimit } from '../../helpers/crosspostCheckoutConstants';
import embedFormControls, { uniqueNameValidator } from '../forms/MultimediaForm';

const MultimediaForm = ({
  pressReleaseId = null,
  multimediaAddOns = [],
  onFileUpload = () => {},
  onFileRemove = () => {},
  onCaptionUpdate = () => {},
  fileRejections = [],
  onFileRejectionAdd = () => {},
  onFileRejectionRemove = () => {},
  onFileRejectionsReset = () => {},
}) => {
  const api = new MultimediaService(pressReleaseId);

  // eslint-disable-next-line object-curly-newline
  const { fields, setField, validateField, validateForm } = useFormControls(embedFormControls);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      const remainingFiles = fileLimit - multimediaAddOns.length;
      setIsSubmitting(true);

      // Clearing previous round of fileRejections/MultimediaUploadErrorBanner
      onFileRejectionsReset();
      // flattening rejectedFiles array
      if (rejectedFiles.length > 0) {
        setIsSubmitting(false);
        const flatFileRejections = flattenFileRejections(rejectedFiles);
        onFileRejectionAdd(flatFileRejections);
      }

      // Checking for duplicate file names
      const dedupedFiles = [];
      acceptedFiles.forEach((file) => {
        const error = uniqueNameValidator(file, multimediaAddOns);
        if (error) {
          onFileRejectionAdd([error]);
        } else {
          dedupedFiles.push(file);
        }
      });

      // Checking if file limit is reached
      if (dedupedFiles.length > remainingFiles) {
        const excessFileRejections = [];
        acceptedFiles.splice(remainingFiles).forEach((file) => {
          excessFileRejections.push({ fileName: file.name, error: 'too-many-files' });
        });
        onFileRejectionAdd(excessFileRejections);
      }

      // Uploading final accepted files
      if (dedupedFiles.length > 0) {
        try {
          const response = await api.uploadFiles(dedupedFiles.slice(0, remainingFiles));
          onFileUpload(response);
        } catch (error) {
          setIsSubmitting(false);
          onFileRejectionAdd([{ fileName: '', error: 'server-side-error' }]);
        }
      }
      setIsSubmitting(false);
    },
    [multimediaAddOns, onFileUpload, onFileRejectionAdd, onFileRejectionsReset],
  );

  const handleFileRemoval = async (addOnId) => {
    await api.deactivateFile(addOnId);
    onFileRemove(addOnId);
  };

  const handleCaptionUpdate = (e, addOnId) => {
    onCaptionUpdate(addOnId, e.target.value);
  };

  const submitCaption = async (addOnId) => {
    // eslint-disable-next-line max-len
    const addOn = multimediaAddOns.find((item) => item.id === addOnId);
    if (addOn) {
      await api.updateCaption(addOnId, addOn.caption);
    }
  };

  const handleEmbedUrlSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    onFileRejectionsReset();
    try {
      const isValid = validateForm();
      if (!isValid) {
        onFileRejectionAdd([{ fileName: '', error: 'invalid-url' }]);
        return;
      }
      const response = await api.uploadEmbedUrl(fields.embedUrl.value);
      onFileUpload([response]);
    } catch (error) {
      onFileRejectionAdd([{ fileName: '', error: 'server-side-error' }]);
    } finally {
      setIsSubmitting(false);
      setField({ target: { name: 'embedUrl', value: '' } });
    }
  };

  const config = {
    validator: uniqueNameValidator,
    maxFiles: fileLimit,
    multiple: true,
    noDrag: false,
    onDrop,
  };

  return (
    <>
      {fileRejections.length > 0
        && fileRejections.map((fileRejection) => {
          return (
            <MultimediaUploadErrorBanner
              error={fileRejection.error}
              fileName={fileRejection.fileName}
              handleBannerClose={() => onFileRejectionRemove(fileRejection)}
              key={`${fileRejection.fileName}-${fileRejection.error}`}
            />
          );
        })}
      <FileDropZone
        config={config}
        mediaFileLimitReached={multimediaAddOns.length >= fileLimit}
        buttonClass="dropzone__button"
        buttonText="Choose file"
        containerClass="dropzone__container"
        iconColor="#000"
      >
        <p className="dropzone__text" style={{ opacity: multimediaAddOns.length >= fileLimit ? '0.5' : '1' }}>
          Drag and drop your logo here
        </p>
        <p className="dropzone__text" style={{ opacity: multimediaAddOns.length >= fileLimit ? '0.5' : '1' }}>
          or
        </p>
      </FileDropZone>
      <div className="help-text">Supported format: jpg, png, pdf, mp4, and mov</div>

      <section id="embed-upload">
        <h3 className="multimedia-subheading">Embed video from URL</h3>
        <EmbedInput
          disabled={multimediaAddOns.length >= fileLimit}
          handleChange={validateField}
          handleSubmit={handleEmbedUrlSubmit}
          name="embedUrl"
          value={fields.embedUrl.value}
        />
      </section>

      {multimediaAddOns.length > 0 && (
        <section className="media-gallery">
          <h3 className="multimedia-subheading">Media gallery</h3>
          <UploadedFileContainer
            containerClass="file-item-container"
            counterClass="help-text"
            currentMultimediaAddOns={multimediaAddOns}
            handleFileRemoval={handleFileRemoval}
            handleCaptionUpdate={handleCaptionUpdate}
            sectionHeading="Media gallery:"
            sectionHeadingClass="multimedia-subheading"
            submitCaption={submitCaption}
          />
        </section>
      )}
      {isSubmitting && <LoadingScreen isSubmitting={isSubmitting} />}
    </>
  );
};

MultimediaForm.propTypes = {
  pressReleaseId: PropTypes.number,
  multimediaAddOns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    file_name: PropTypes.string,
    caption: PropTypes.string,
    media_type: PropTypes.string,
    secure_url: PropTypes.string,
  })),
  onFileUpload: PropTypes.func,
  onFileRemove: PropTypes.func,
  onCaptionUpdate: PropTypes.func,
  fileRejections: PropTypes.arrayOf(PropTypes.shape({
    fileName: PropTypes.string,
    error: PropTypes.string,
  })),
  onFileRejectionAdd: PropTypes.func,
  onFileRejectionRemove: PropTypes.func,
  onFileRejectionsReset: PropTypes.func,
};

export default MultimediaForm;
